/**
 * Market-level signals for alert rules.
 *
 * The legacy alert system carried two kinds of rule: per-contract comparisons, and rules over
 * dataset-level analysis (most-active option, volume peaks, sentiment areas, SPX summary, gamma).
 * The second kind was computed inside the alert context and could not be expressed as a condition
 * on a row, which is why several of those presets never fired — their "field" simply did not exist
 * on any record.
 *
 * This module ports that analysis into pure functions over the scanner dataset and emits it as a
 * single record. Rules can then be written against those signals with the same builder as anything
 * else, and the record's identity is the signal values themselves, so an alert fires when a signal
 * changes rather than on every 30-second pass.
 */

export interface MarketSignals extends Record<string, unknown> {
  /** 'bullish' | 'bearish' | '' — SPX, today's expiry, by sentiment majority. */
  market_summary_change: string;
  spx_today_sentiment: string;
  most_active_option: string;
  most_active_direction: string;
  most_active_volume: number | null;
  volume_peak: string;
  volume_peak_symbol: string;
  volume_peak_strike: number | null;
  volume_peak_volume: number | null;
  sentiment_area: string;
  spot_gamma_max: number | null;
  spot_gamma_max_strike: number | null;
  premium_max: number | null;
  premium_max_symbol: string;
  premium_min: number | null;
  premium_min_symbol: string;
  delta_max: number | null;
  delta_max_symbol: string;
  delta_min: number | null;
  delta_min_symbol: string;
  /** Per-underlying verdicts joined into one searchable string, e.g. "SPY: Bullish at Support". */
  underlying_sentiment: string;
  /** Contracts the signals were computed from; 0 means nothing was computable. */
  sample_size: number;
}

type Row = Record<string, unknown>;

function n(v: unknown): number {
  const x = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(x) ? x : 0;
}

function s(v: unknown): string {
  return v == null ? '' : String(v);
}

function underlyingOf(row: Row): string {
  return s(row.underlyingSymbol || row.Underlying || row.Symbol || 'Unknown');
}

/** SPX, today's expiry: which way does sentiment lean? Ported from analyzeMarketSummary. */
function spxTodaySentiment(rows: Row[]): string {
  const spx = rows.filter(
    (r) => underlyingOf(r).includes('SPX') || s(r.Symbol).includes('SPX'),
  );
  if (!spx.length) return '';
  const today = new Date().toISOString().split('T')[0];
  const todays = spx.filter((r) => s(r['Expiry Date']) === today);
  if (!todays.length) return '';
  const bullish = todays.filter((r) => s(r.Sentiment).toLowerCase().includes('bullish')).length;
  const bearish = todays.filter((r) => s(r.Sentiment).toLowerCase().includes('bearish')).length;
  if (bullish > bearish) return 'bullish';
  if (bearish > bullish) return 'bearish';
  return '';
}

/** Ported from analyzeMostActive: ask volume = buying, bid volume = selling. */
function mostActive(rows: Row[]): { symbol: string; volume: number; direction: string } | null {
  let best: { symbol: string; volume: number; direction: string } | null = null;
  for (const r of rows) {
    const volume = n(r.Volume);
    if (volume <= 0) continue;
    if (best && volume <= best.volume) continue;
    const ask = n(r['Ask Volume']);
    const bid = n(r['Bid Volume']);
    best = {
      symbol: s(r.Symbol),
      volume,
      direction: ask > bid ? 'bought' : bid > ask ? 'sold' : 'neutral',
    };
  }
  return best;
}

/** Strikes within 5% of spot, ported from analyzeVolumePeaks / analyzeSentimentAreas. */
function nearSpot(rows: Row[]): { rows: Row[]; spot: number } {
  const priced = rows.filter((r) => n(r['Underlying Price']) > 0);
  if (!priced.length) return { rows: [], spot: 0 };
  const spot = n(priced[0]['Underlying Price']);
  return {
    rows: priced.filter((r) => Math.abs(n(r.Strike) - spot) / spot < 0.05),
    spot,
  };
}

function volumePeak(rows: Row[]): { symbol: string; strike: number; volume: number } | null {
  const { rows: near } = nearSpot(rows);
  let best: { symbol: string; strike: number; volume: number } | null = null;
  for (const r of near) {
    const volume = n(r.Volume);
    if (volume <= 0) continue;
    if (best && volume <= best.volume) continue;
    best = { symbol: s(r.Symbol), strike: n(r.Strike), volume };
  }
  return best;
}

function sentimentArea(rows: Row[]): string {
  const { rows: near } = nearSpot(rows);
  if (!near.length) return '';
  const bullish = near.filter((r) => s(r.Sentiment).toLowerCase().includes('bullish')).length;
  const bearish = near.filter((r) => s(r.Sentiment).toLowerCase().includes('bearish')).length;
  if (bearish > bullish) return 'bearish';
  if (bullish > bearish) return 'bullish';
  return '';
}

/** Ported from analyzeGamma: gamma × spot² × 0.01 × direction × OI, peak across strikes. */
function spotGammaPeak(rows: Row[]): { value: number; strike: number } | null {
  const usable = rows.filter((r) => n(r.Gamma) > 0 && n(r['Open Interest']) > 0 && n(r['Underlying Price']) > 0);
  if (!usable.length) return null;
  const spot = n(usable[0]['Underlying Price']);
  let best: { value: number; strike: number } | null = null;
  for (const r of usable) {
    const opt = s(r['Option Type'] || r.optionType).toUpperCase();
    const direction = opt === 'C' || opt === 'CALL' ? 1 : -1;
    const value = n(r.Gamma) * spot * spot * 0.01 * direction * n(r['Open Interest']);
    if (!best || value > best.value) best = { value, strike: n(r.Strike) };
  }
  return best;
}

function extreme(rows: Row[], field: string): { max: number | null; maxSym: string; min: number | null; minSym: string } {
  let max: number | null = null;
  let min: number | null = null;
  let maxSym = '';
  let minSym = '';
  for (const r of rows) {
    const raw = r[field];
    if (raw == null || raw === '') continue;
    const v = n(raw);
    if (!Number.isFinite(v)) continue;
    if (max === null || v > max) {
      max = v;
      maxSym = s(r.Symbol);
    }
    if (min === null || v < min) {
      min = v;
      minSym = s(r.Symbol);
    }
  }
  return { max, maxSym, min, minSym };
}

/**
 * Ported from analyzeUnderlyingSentiment. Bullish/bearish by majority, plus a support/resistance
 * note when the average price sits in the bottom or top tenth of the observed range.
 */
function underlyingSentiment(rows: Row[]): string {
  const groups = new Map<string, Row[]>();
  for (const r of rows) {
    const key = underlyingOf(r);
    const list = groups.get(key);
    if (list) list.push(r);
    else groups.set(key, [r]);
  }

  const verdicts: string[] = [];
  groups.forEach((items, underlying) => {
    const bullish = items.filter((r) => {
      const sent = s(r.Sentiment).toLowerCase();
      const dir = s(r.Direction).toLowerCase();
      return sent.includes('bullish') || dir.includes('up') || dir.includes('bull');
    }).length;
    const bearish = items.filter((r) => {
      const sent = s(r.Sentiment).toLowerCase();
      const dir = s(r.Direction).toLowerCase();
      return sent.includes('bearish') || dir.includes('down') || dir.includes('bear');
    }).length;
    const total = items.length || 1;
    const bullRatio = bullish / total;
    const bearRatio = bearish / total;

    let verdict = 'Neutral';
    if (bullRatio > 0.6) verdict = 'Bullish';
    else if (bearRatio > 0.6) verdict = 'Bearish';
    else if (Math.abs(bullRatio - bearRatio) < 0.2) verdict = 'Ranging';

    const prices = items.map((r) => n(r['Underlying Price'])).filter((p) => p > 0);
    if (prices.length) {
      const avg = prices.reduce((a, b) => a + b, 0) / prices.length;
      const lo = Math.min(...prices);
      const hi = Math.max(...prices);
      const range = hi - lo;
      if (avg <= lo + range * 0.1) verdict += ' at Support';
      else if (avg >= hi - range * 0.1) verdict += ' at Resistance';
    }
    verdicts.push(`${underlying}: ${verdict}`);
  });

  return verdicts.sort().join(' · ');
}

export function computeMarketSignals(rows: Row[]): MarketSignals {
  const active = mostActive(rows);
  const peak = volumePeak(rows);
  const gamma = spotGammaPeak(rows);
  const prem = extreme(rows, 'Total Premium');
  const delta = extreme(rows, 'Delta');
  const spx = spxTodaySentiment(rows);

  return {
    market_summary_change: spx,
    spx_today_sentiment: spx,
    most_active_option: active?.symbol ?? '',
    most_active_direction: active?.direction ?? '',
    most_active_volume: active?.volume ?? null,
    volume_peak: peak ? `${peak.symbol} ${peak.strike}` : '',
    volume_peak_symbol: peak?.symbol ?? '',
    volume_peak_strike: peak?.strike ?? null,
    volume_peak_volume: peak?.volume ?? null,
    sentiment_area: sentimentArea(rows),
    spot_gamma_max: gamma?.value ?? null,
    spot_gamma_max_strike: gamma?.strike ?? null,
    premium_max: prem.max,
    premium_max_symbol: prem.maxSym,
    premium_min: prem.min,
    premium_min_symbol: prem.minSym,
    delta_max: delta.max,
    delta_max_symbol: delta.maxSym,
    delta_min: delta.min,
    delta_min_symbol: delta.minSym,
    underlying_sentiment: underlyingSentiment(rows),
    sample_size: rows.length,
  };
}

/**
 * Identity of a signals snapshot: the values themselves. A rule therefore fires when a signal
 * changes and stays quiet while it holds, which is what the legacy "…_change" alerts intended.
 */
export function marketSignalsKey(sig: MarketSignals): string {
  return [
    sig.market_summary_change,
    sig.most_active_option,
    sig.most_active_direction,
    sig.volume_peak,
    sig.sentiment_area,
    sig.spot_gamma_max_strike,
    sig.premium_max_symbol,
    sig.delta_max_symbol,
    sig.underlying_sentiment,
  ].join('|');
}
